import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getGrantsByIds, updateGrant} from 'src/openapi/admin/apis/EntitlementsGrantApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    DEFAULT_GRANT,

    GRANT_DESCRIPTION_CONTROL,
    GRANT_DESCRIPTION_REQUIRED_ERROR,
    GRANT_DESCRIPTION_REQUIRED_MESSAGE,

    GRANT_NAME_CONTROL,
    GRANT_NAME_REQUIRED_ERROR,
    GRANT_NAME_REQUIRED_MESSAGE,
} from './constants';

import type {Grant} from 'src/openapi/admin/models/Grant';
import type {ErrorPayload} from 'src/typedef';

type Props = {
    grantId: string,
};

export const useUpdateGrant = ({grantId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);

    const getDispatchAction = useCallback(async () => {
        const response = await apiDispatch(getGrantsByIds({requestBody: [grantId]}));
        if (response.payload.success) {
            if (!response.payload.data[grantId]) {
                return Promise.resolve({type: '', payload: {
                    success: false,
                    error: true,
                    data: {message: 'Grant not found', status: 404},
                } as ErrorPayload});
            }

            const payload: {success: true, error: false, data: Grant} = {
                success: true,
                error: false,
                data: response.payload.data[grantId],
            };
            return Promise.resolve({type: '', payload});
        } else {
            return Promise.resolve({type: '', payload: response.payload});
        }
    }, [grantId, apiDispatch]);

    const saveDispatchAction = useCallback((updateGrantRequest: Grant, signal: AbortSignal) => apiDispatch(
        updateGrant({grantId, updateGrantRequest}, {signal}),
    ), [grantId, apiDispatch]);

    const editValidation = useCallback((data: Grant) => {
        if (data.name) {
            removeError(GRANT_NAME_CONTROL);
        }

        if (data.description) {
            removeError(GRANT_DESCRIPTION_CONTROL);
        }
    }, [removeError]);

    const saveValidation = useCallback((data: Grant) => {
        let valid = true;

        if (!data.name) {
            addError({
                controlId: GRANT_NAME_CONTROL,
                code: GRANT_NAME_REQUIRED_ERROR,
                message: GRANT_NAME_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!data.description) {
            addError({
                controlId: GRANT_DESCRIPTION_CONTROL,
                code: GRANT_DESCRIPTION_REQUIRED_ERROR,
                message: GRANT_DESCRIPTION_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        return valid;
    }, [addError]);

    return useCommonDataHook({
        initValue: DEFAULT_GRANT,
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
